import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, FileCheck2, ListChecks, PenSquare, Sparkles, Mail } from "lucide-react";
import { useDocMeta } from "../../lib/useDocMeta";
import { track, EVENT } from "../../lib/publicAnalytics";
import Navbar from "../../components/Navbar";

const TIERS = [
  {
    key: "review",
    icon: FileCheck2,
    eyebrow: "TIER 01",
    title: "Proposal Review / Compliance Check",
    lede: "An independent Section L / M audit of a proposal you already have in draft.",
    bullets: [
      "Line-by-line Section L (Instructions to Offerors) verification",
      "Section M (Evaluation Factors) alignment check with weighted scoring",
      "Format, page-limit, and cross-referencing audit",
      "Written recommendation memo with prioritized fixes",
    ],
    turnaround: "48–72 hours",
    fit: "For teams that produced their own draft and want a defensible outside review before submission.",
  },
  {
    key: "matrix",
    icon: ListChecks,
    eyebrow: "TIER 02",
    title: "Compliance Matrix / Proposal Blueprint",
    lede: "A complete compliance matrix and response outline before you start writing.",
    bullets: [
      "Every Section L instruction mapped to response owner, location, and weight",
      "Every Section M criterion mapped to volumes, sections, and page allocation",
      "Response outline anchored to evaluation criteria",
      "Capture-risk register (top 5 pursuit risks + mitigation notes)",
    ],
    turnaround: "3 business days",
    fit: "For teams that will write in-house but want a rigorous compliance skeleton and pursuit plan first.",
  },
  {
    key: "development",
    icon: PenSquare,
    eyebrow: "TIER 03",
    title: "Full Proposal Development",
    lede: "End-to-end founder-led proposal development on a single solicitation.",
    bullets: [
      "Opportunity intelligence + Bid/No-Bid recommendation memo",
      "Complete compliance matrix and response outline",
      "Response draft (Technical, Management, Past Performance)",
      "Compliance & format verification statement + 30-minute delivery walkthrough",
    ],
    turnaround: "7 business days (Standard) / 10 business days (Premium)",
    fit: "For teams that need the full pursuit built and delivered inside a fixed price and fixed timeline.",
  },
];

const ADDONS = [
  { title: "Executive Summary Development", note: "Standalone executive-summary authoring aligned to Section M." },
  { title: "Technical Narrative (per volume)", note: "Focused technical or management narrative development." },
  { title: "Management Plan Development", note: "Staffing plan, transition, quality, and risk management narratives." },
  { title: "Proposal Readiness Assessment", note: "Portfolio-level or opportunity-specific readiness diagnostic." },
  { title: "Oral-Presentation Preparation Deck", note: "Structured coaching deck and rehearsal for shortlist events (Premium)." },
  { title: "Extended Capture Strategy Session", note: "90-minute working session for complex, high-value pursuits (Premium)." },
];

const HOW = [
  "Book a 25-minute Strategy Call — we discuss the specific pursuit.",
  "You receive a fixed-price, fixed-scope Statement of Work within 24 hours.",
  "50% deposit → engagement begins. 50% due on delivery.",
  "Founder-led execution — no hand-offs, no rotating account managers.",
];

export default function ProposalServices() {
  useDocMeta({
    title: "Proposal Services · BidNoBidIQ",
    description: "Federal Opportunity Intelligence & Proposal Consulting. Founder-led. Fixed-scope. Fixed-price.",
    path: "/proposal-services",
  });
  useEffect(() => { track(EVENT.PROPOSAL_SERVICES_VIEWED || "public_proposal_services_viewed", {}); }, []);

  return (
    <>
      <Navbar />
      <div className="max-w-[1100px] mx-auto px-6 py-12" data-testid="proposal-services-page">
      <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-3">/ Proposal Services</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-slate-100 leading-tight mb-4" data-testid="proposal-services-title">
        Federal Opportunity Intelligence &amp; Proposal Consulting.
      </h1>
      <p className="text-slate-400 text-lg max-w-3xl leading-relaxed mb-10">
        Founder-led consulting for small federal contractors. Every engagement combines structured
        AI-assisted contract intelligence with 18+ years of federal contracting, acquisition, procurement,
        and proposal experience. Fixed scope. Fixed price. No hourly billing. No win-fee arrangements.
      </p>

      {/* Release 0.1 outcomes — what customers actually walk away with */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border-soft)] mb-16" data-testid="proposal-services-outcomes">
        <Outcome
          kicker="Decision"
          title="A clear Go/No-Go"
          body="Every opportunity ends with an explicit GO · GO WITH CAUTION · NO GO · INSUFFICIENT DATA verdict — and the confidence we have in that call."
          testid="outcome-go-no-go"
        />
        <Outcome
          kicker="Executive"
          title="A one-page brief"
          body="Printable, downloadable Executive Opportunity Brief covering agency, ceiling, due date, verdict, risks, compliance, and next actions — ready for your leadership team."
          testid="outcome-brief"
        />
        <Outcome
          kicker="Shareable"
          title="Send to partners"
          body="One-click, watermarked, expiring share links let you send the brief to teaming partners or executives without giving them app access."
          testid="outcome-share"
        />
        <Outcome
          kicker="Delivery"
          title="Proposal support on demand"
          body="Request proposal writing, capture, compliance review, or pricing help from inside your pursuit. Founder-led delivery, tracked to a completion state."
          testid="outcome-delivery"
        />
      </div>

      <div className="grid md:grid-cols-3 gap-px bg-[var(--border-soft)] mb-16" data-testid="proposal-services-tiers">
        {TIERS.map((t) => {
          const Icon = t.icon;
          return (
            <div key={t.key} className="bg-[var(--bg-secondary)] p-6 flex flex-col" data-testid={`proposal-tier-${t.key}`}>
              <div className="flex items-center gap-2 mb-3">
                <Icon className="w-4 h-4 text-sky-400" />
                <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400">{t.eyebrow}</div>
              </div>
              <h2 className="font-display font-bold text-slate-100 text-xl leading-tight mb-2">{t.title}</h2>
              <p className="text-slate-400 text-sm leading-relaxed mb-4">{t.lede}</p>
              <ul className="space-y-2 mb-4 flex-1">
                {t.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-slate-300 text-sm leading-relaxed">
                    <Check className="w-3.5 h-3.5 text-emerald-400 mt-1 shrink-0" strokeWidth={2.5} />
                    {b}
                  </li>
                ))}
              </ul>
              <div className="pt-3 mt-auto border-t border-[var(--border-soft)] space-y-1">
                <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500">Turnaround</div>
                <div className="text-slate-200 text-sm">{t.turnaround}</div>
                <div className="text-slate-500 text-xs leading-relaxed pt-2 italic">{t.fit}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mb-16" data-testid="proposal-services-addons">
        <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-3">/ Optional Add-Ons</div>
        <h2 className="font-display font-bold text-slate-100 text-2xl sm:text-3xl mb-6">Targeted engagements for specific needs.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border-soft)]">
          {ADDONS.map((a) => (
            <div key={a.title} className="bg-[var(--bg-secondary)] p-5">
              <div className="flex items-start gap-2">
                <Sparkles className="w-3.5 h-3.5 text-sky-400 mt-1 shrink-0" />
                <div>
                  <div className="text-slate-100 font-semibold text-sm mb-1">{a.title}</div>
                  <div className="text-slate-400 text-xs leading-relaxed">{a.note}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-px bg-[var(--border-soft)] mb-16">
        <div className="bg-[var(--bg-secondary)] p-6" data-testid="proposal-services-how">
          <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-3">/ How Engagements Work</div>
          <h2 className="font-display font-bold text-slate-100 text-xl mb-4">Four steps. No surprises.</h2>
          <ol className="space-y-3">
            {HOW.map((h, i) => (
              <li key={h} className="flex items-start gap-3 text-slate-300 text-sm leading-relaxed">
                <span className="font-mono text-[10px] text-sky-400 border border-sky-500/40 px-1.5 py-0.5 mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                {h}
              </li>
            ))}
          </ol>
        </div>
        <div className="bg-[var(--bg-secondary)] p-6" data-testid="proposal-services-sample-card">
          <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-3">/ See the Work</div>
          <h2 className="font-display font-bold text-slate-100 text-xl mb-3">Sample Opportunity Intelligence Report.</h2>
          <p className="text-slate-400 text-sm leading-relaxed mb-4">
            A fully-synthetic Opportunity Intelligence Report — exactly the format real customers receive.
            Every name, number, and agency is fictitious.
          </p>
          <Link to="/sample" data-testid="proposal-services-view-sample" className="inline-flex items-center gap-2 border border-sky-500/60 hover:bg-sky-500/10 text-sky-300 font-mono text-[11px] tracking-widest uppercase px-4 py-2.5">
            View Sample Report <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      <div className="border border-sky-500/40 bg-sky-500/5 p-8 flex flex-col md:flex-row items-start md:items-center gap-6" data-testid="proposal-services-cta-band">
        <div className="flex-1">
          <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-2">/ Ready?</div>
          <h2 className="font-display font-bold text-slate-100 text-2xl sm:text-3xl leading-tight mb-2">Talk to us about your next pursuit.</h2>
          <p className="text-slate-400 text-sm leading-relaxed">A 25-minute Strategy Call — no pitch deck, no obligation. Or email us directly.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/strategy-call" data-testid="proposal-services-book-call" className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[11px] tracking-widest uppercase px-5 py-3 font-bold">
            Book a Strategy Call <ArrowRight className="w-3 h-3" />
          </Link>
          <a href="mailto:BidNoBidIQ@aol.com" data-testid="proposal-services-email" className="inline-flex items-center gap-2 border border-sky-500/60 hover:bg-sky-500/10 text-sky-300 font-mono text-[11px] tracking-widest uppercase px-5 py-3">
            <Mail className="w-3.5 h-3.5" /> BidNoBidIQ@aol.com
          </a>
        </div>
      </div>

      <p className="text-slate-500 text-xs leading-relaxed mt-8 max-w-3xl italic">
        This page describes consulting services provided by BidNoBidIQ. It is not a claim of past performance,
        award, or win-rate. The Sample Opportunity Intelligence Report is fully synthetic and does not
        reflect any real customer engagement.
      </p>
    </div>
    </>
  );
}

function Outcome({ kicker, title, body, testid }) {
  return (
    <div className="bg-[var(--bg-secondary)] p-5 flex flex-col gap-2" data-testid={testid}>
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-sky-400">{kicker}</div>
      <div className="font-display font-bold text-slate-100 text-lg leading-tight">{title}</div>
      <p className="text-slate-400 text-sm leading-relaxed">{body}</p>
    </div>
  );
}

